import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import {
  brand,
  dark,
  derived,
  light,
  semantic,
  stateDark,
  stateLight,
} from './palette';

/**
 * The Aeven portal themes. Every colour comes from `./palette`, which holds the
 * specification and the shades derived from it; nothing is written as a literal
 * here. Typography is Satoshi over IBM Plex Mono, both served from
 * `packages/app/public/fonts` so a page can be branded without reaching a font
 * CDN first.
 */
const typography = {
  fontBody: '"Satoshi", ui-sans-serif, system-ui, -apple-system, sans-serif',
  fontMono:
    '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

const shape = {
  radiusCard: 10,
  radiusButton: 6,
  radiusChip: 4,
};

const componentOverrides = {
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: shape.radiusCard },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: shape.radiusCard },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: shape.radiusButton,
        textTransform: 'none' as const,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: shape.radiusButton },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: shape.radiusChip },
    },
  },
};

/**
 * Warm Light and its dark-theme counterpart exist to separate a table header
 * from the rows under it. Without this the specification's lightest three
 * colours would all land on backgrounds nobody can tell apart.
 */
const tableHead = (background: string) => ({
  MuiTableHead: {
    styleOverrides: {
      root: { backgroundColor: background },
    },
  },
});

export const aevenLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: light.primary },
    secondary: { main: semantic.accent },
    error: { main: stateLight.error },
    warning: { main: stateLight.warning },
    success: { main: stateLight.success },
    info: { main: stateLight.info },
    background: { default: light.bg, paper: light.surface },
    text: { primary: light.text, secondary: light.textMuted },
    divider: light.border,
    // Backstage carries its own link blue (#0A6EBE) in `palettes.light`, which
    // survives a primary override and puts a stock blue on every link in an
    // otherwise Bronze Green UI. It has to be set explicitly. Navy rather than
    // the primary, because at 8.84:1 on Pale it is the most readable colour
    // the specification has, and links are what people aim at.
    link: light.link,
    navigation: {
      background: light.navBg,
      indicator: semantic.accent,
      color: light.navColor,
      selectedColor: light.navSelected,
      navItem: { hoverBackground: light.navHover },
      submenu: { background: light.navSubmenu },
    },
  },
  fontFamily: typography.fontBody,
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [brand.bronzeGreen, derived.bronzeShadow],
      shape: shapes.wave,
    }),
  },
  components: { ...componentOverrides, ...tableHead(light.surfaceAlt) },
});

export const aevenDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: dark.primary },
    secondary: { main: semantic.accent },
    error: { main: stateDark.error },
    warning: { main: stateDark.warning },
    success: { main: stateDark.success },
    info: { main: stateDark.info },
    background: { default: dark.bg, paper: dark.surface },
    text: { primary: dark.text, secondary: dark.textMuted },
    divider: dark.border,
    link: dark.link,
    navigation: {
      background: dark.navBg,
      indicator: semantic.accent,
      color: dark.navColor,
      selectedColor: dark.navSelected,
      navItem: { hoverBackground: dark.navHover },
      submenu: { background: dark.navSubmenu },
    },
  },
  fontFamily: typography.fontBody,
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [derived.bronzeShadow, derived.charcoalSunk],
      shape: shapes.wave,
    }),
  },
  components: { ...componentOverrides, ...tableHead(dark.surfaceAlt) },
});

export { typography as aevenTypography };
